/**
 * deadStock.ts — Pure computation of slow-moving and dead inventory: where the
 * shop's capital is locked in stock that isn't selling.
 *
 * For each stocked product it weighs the money tied up (units × purchase price)
 * against how fast it moves (units sold over a lookback window), and classifies:
 *
 *  - **DEAD**   — no net sales in the window at all.
 *  - **SLOW**   — selling, but so slowly the current stock would take longer than
 *                 `slowCoverDays` to clear at that rate (over-stocked / stale).
 *  - **MOVING** — turning over at a healthy pace.
 *
 * "Units sold" is net of returns, so a sale that was fully returned doesn't make
 * dead stock look alive. Capital is valued at `purchase_price` (cost), which is
 * the right basis for a *current* valuation — the mutating-price concern only
 * affects historical margin, not what the stock is worth to liquidate today.
 *
 * Pure — see `deadStockDataSource.ts` for the WatermelonDB bridge.
 */

export type StockStatus = 'DEAD' | 'SLOW' | 'MOVING';

export function round2(n: number): number {
  return Math.round((n + Number.EPSILON) * 100) / 100;
}

const DAY_MS = 24 * 60 * 60 * 1000;

// ─── Inputs ──────────────────────────────────────────────────────────────────

export interface DeadStockProductInput {
  id: string;
  name: string;
  category: string;
  currentStock: number;
  purchasePrice: number;
  sellingPrice: number;
  /** Net units sold over the lookback window (sales − returns). */
  unitsSold: number;
  /** Date of the most recent sale, or null if none in the window. */
  lastSoldAt: Date | null;
}

export interface DeadStockInput {
  products: DeadStockProductInput[];
  /** Length of the sales lookback window, in days. */
  windowDays: number;
  /** Stock that would take longer than this to clear at the window's rate is SLOW. */
  slowCoverDays: number;
  asOf: Date;
}

// ─── Output ──────────────────────────────────────────────────────────────────

export interface DeadStockItem {
  productId: string;
  name: string;
  category: string;
  currentStock: number;
  /** Capital locked at cost: currentStock × purchasePrice. */
  stockValue: number;
  /** Retail value at the selling price, for context. */
  retailValue: number;
  unitsSold: number;
  /** Whole days since the last sale; null if never sold in the window. */
  daysSinceSale: number | null;
  /** Projected days to clear current stock at the window's sale rate; null if DEAD. */
  daysOfCover: number | null;
  status: StockStatus;
}

export interface StatusSummary {
  count: number;
  stockValue: number;
}

export interface DeadStockReport {
  /** All stocked products, worst (most capital, slowest) first. */
  items: DeadStockItem[];
  dead: StatusSummary;
  slow: StatusSummary;
  moving: StatusSummary;
  /** Total capital in stock across all statuses. */
  totalStockValue: number;
  windowDays: number;
  slowCoverDays: number;
}

function classify(
  unitsSold: number,
  currentStock: number,
  windowDays: number,
  slowCoverDays: number,
): { status: StockStatus; daysOfCover: number | null } {
  if (unitsSold <= 0) return { status: 'DEAD', daysOfCover: null };
  const perDay = unitsSold / windowDays;
  const daysOfCover = perDay > 0 ? currentStock / perDay : null;
  if (daysOfCover != null && daysOfCover > slowCoverDays) return { status: 'SLOW', daysOfCover };
  return { status: 'MOVING', daysOfCover };
}

const STATUS_RANK: Record<StockStatus, number> = { DEAD: 0, SLOW: 1, MOVING: 2 };

export function buildDeadStock(input: DeadStockInput): DeadStockReport {
  const { products, windowDays, slowCoverDays, asOf } = input;

  const items: DeadStockItem[] = products
    .filter((p) => p.currentStock > 0)
    .map((p) => {
      const { status, daysOfCover } = classify(p.unitsSold, p.currentStock, windowDays, slowCoverDays);
      const daysSinceSale = p.lastSoldAt
        ? Math.max(0, Math.floor((asOf.getTime() - p.lastSoldAt.getTime()) / DAY_MS))
        : null;
      return {
        productId: p.id,
        name: p.name,
        category: p.category,
        currentStock: p.currentStock,
        stockValue: round2(p.currentStock * p.purchasePrice),
        retailValue: round2(p.currentStock * p.sellingPrice),
        unitsSold: round2(p.unitsSold),
        daysSinceSale,
        daysOfCover: daysOfCover != null ? Math.round(daysOfCover) : null,
        status,
      };
    })
    // Worst first: dead before slow before moving, then most capital locked.
    .sort((a, b) => STATUS_RANK[a.status] - STATUS_RANK[b.status] || b.stockValue - a.stockValue);

  const summary = (status: StockStatus): StatusSummary => {
    const rows = items.filter((i) => i.status === status);
    return { count: rows.length, stockValue: round2(rows.reduce((s, i) => s + i.stockValue, 0)) };
  };

  return {
    items,
    dead: summary('DEAD'),
    slow: summary('SLOW'),
    moving: summary('MOVING'),
    totalStockValue: round2(items.reduce((s, i) => s + i.stockValue, 0)),
    windowDays,
    slowCoverDays,
  };
}
